import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { getTrialBalance } from "./trialBalanceService";
import TrialBalanceTable from "./TrialBalanceTable";
import {
  FaBalanceScale,
  FaPrint,
  FaArrowLeft,
  FaCheckCircle,
  FaExclamationTriangle,
  FaFilter,
  FaUndo,
  FaChartLine
} from "react-icons/fa";
import "./TrialBalance.css";
import { usePermissions } from "../../context/PermissionContext";
import AccessDenied from "../ui/AccessDenied";

const fmt = (n) =>
  Number(n || 0).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const TrialBalancePage = () => {
  const navigate = useNavigate();
  const { canView, canExport } = usePermissions();

  if (!canView("trial_balance")) {
    return <AccessDenied moduleName="Trial Balance" action="view" />;
  }

  // ── Filters ───────────────────────────────────────────
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate]     = useState("");

  // ── Data ──────────────────────────────────────────────
  const [accounts, setAccounts] = useState([]);
  const [summary, setSummary]   = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  const hasFetched = useRef(false);

  // ── Fetch ──────────────────────────────────────────────
  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const filters = {
        start_date: startDate || undefined,
        end_date:   endDate   || undefined,
      };
      const data = await getTrialBalance(filters);
      setAccounts(data.accounts || []);
      setSummary(data.summary   || {});
    } catch (err) {
      console.error("Trial Balance fetch error:", err);
      setError("Failed to load the Trial Balance. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (!hasFetched.current) {
      hasFetched.current = true;
      fetchReport();
    }
  }, [fetchReport]);

  // ── Reset ──────────────────────────────────────────────
  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setTimeout(() => fetchReport(), 0);
  };

  // ── Print ──────────────────────────────────────────────
  const handlePrint = () => window.print();

  const isBalanced = summary?.is_balanced;

  return (
    <div className="tb-page">
      {/* ── Header ─────────────────────────────────────── */}
      <div className="tb-header">
        <h2>
          <FaBalanceScale style={{ color: "#0284c7" }} /> Trial Balance
        </h2>
        <div className="tb-header-actions">
          <button
            className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1 shadow-sm"
            onClick={() => navigate("/dashboard/reports/trial-balance-comparison")}
          >
            <FaChartLine className="me-1" /> 5 Years Comparison
          </button>
          <button className="tb-btn-print" onClick={handlePrint}>
            <FaPrint className="me-1" /> Print / PDF
          </button>
          <button
            className="tb-btn-back"
            onClick={() => navigate("/dashboard")}
          >
            <FaArrowLeft className="me-1" /> Dashboard
          </button>
        </div>
      </div>

      {/* ── Summary Cards ───────────────────────────────── */}
      {summary && (
        <div className="tb-summary">
          <div className="tb-card card-accounts">
            <div className="card-label">Total Accounts</div>
            <div className="card-value">{summary.total_accounts ?? 0}</div>
          </div>

          <div className="tb-card card-debit">
            <div className="card-label">Total Debit (Dr)</div>
            <div className="card-value">${fmt(summary.total_debit)}</div>
          </div>

          <div className="tb-card card-credit">
            <div className="card-label">Total Credit (Cr)</div>
            <div className="card-value">${fmt(summary.total_credit)}</div>
          </div>

          <div className={`tb-card ${isBalanced ? "card-ok" : "card-err"}`}>
            <div className="card-label">Balance Status</div>
            <div className="card-value d-flex align-items-center gap-1">
              {isBalanced ? (
                <><FaCheckCircle className="text-success me-1" /> Balanced</>
              ) : (
                <><FaExclamationTriangle className="text-danger me-1" /> Difference: ${fmt(Math.abs(summary.difference || 0))}</>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Filters ─────────────────────────────────────── */}
      <div className="tb-filters">
        <div className="filter-group">
          <label>From Date</label>
          <input
            id="tb-start-date"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div className="filter-group">
          <label>To Date</label>
          <input
            id="tb-end-date"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <button id="tb-apply-btn" className="tb-btn-filter" onClick={fetchReport}>
          <FaFilter className="me-1" /> Apply Filters
        </button>
        <button id="tb-reset-btn" className="tb-btn-reset" onClick={handleReset}>
          <FaUndo className="me-1" /> Reset
        </button>
      </div>

      {/* ── Content ─────────────────────────────────────── */}
      {loading ? (
        <div className="tb-loading">
          <div className="spinner-border text-primary mb-2" role="status" />
          <p>Loading trial balance…</p>
        </div>
      ) : error ? (
        <div className="tb-loading text-danger">
          <FaExclamationTriangle className="me-2" /> {error}
        </div>
      ) : (
        <TrialBalanceTable accounts={accounts} summary={summary} />
      )}
    </div>
  );
};

export default TrialBalancePage;
